/**
 * widgets/app-footer/ui/AppFooter — slim legal/contact footer.
 *
 * Renders up to three links (Impressum, Datenschutzerklärung, Kontakt),
 * each sourced from an env var and rendered ONLY when that var is set:
 *   - VITE_IMPRESSUM_URL   -> "Impressum" / "Legal notice"    (external link)
 *   - VITE_PRIVACY_URL     -> "Datenschutzerklärung" / "Privacy policy" (external link)
 *   - VITE_SUPPORT_EMAIL   -> "Kontakt" / "Contact"           (mailto: link)
 *
 * When none of the three env vars are set, the component renders nothing
 * (not even the wrapping <footer>) so it never adds an empty border/strip
 * to the layout.
 *
 * Mounted in both the authenticated AppShell (below <main>'s content) and
 * the public PublicLayout (bottom of the auth pages).
 *
 * FSD: widgets may import from shared only.
 */

import { useTranslation } from 'react-i18next';
import { IMPRESSUM_URL, PRIVACY_URL, SUPPORT_EMAIL } from '@/shared/config/env';
import { TextLink } from '@/shared/ui/text-link';

export function AppFooter() {
  const { t } = useTranslation('common');

  const hasAnyLink = Boolean(IMPRESSUM_URL || PRIVACY_URL || SUPPORT_EMAIL);

  if (!hasAnyLink) {
    return null;
  }

  return (
    <footer
      className="mt-auto border-t px-4 py-4 sm:px-6"
      data-testid="app-footer"
    >
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-center">
        {IMPRESSUM_URL && (
          <TextLink
            href={IMPRESSUM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-muted-foreground decoration-muted-foreground/40 hover:decoration-muted-foreground"
            data-testid="app-footer-impressum"
          >
            {t('footer.legalNotice')}
          </TextLink>
        )}
        {PRIVACY_URL && (
          <TextLink
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-muted-foreground decoration-muted-foreground/40 hover:decoration-muted-foreground"
            data-testid="app-footer-privacy"
          >
            {t('footer.privacyPolicy')}
          </TextLink>
        )}
        {SUPPORT_EMAIL && (
          <TextLink
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-sm text-muted-foreground decoration-muted-foreground/40 hover:decoration-muted-foreground"
            data-testid="app-footer-contact"
          >
            {t('footer.contact')}
          </TextLink>
        )}
      </div>
    </footer>
  );
}
